import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { EnvelopeSimple, PaperPlaneTilt, ArrowsClockwise } from "@phosphor-icons/react";

/** Preview + send the weekly AI-curated digest. Shown on Dashboard. */
export default function DigestPreview() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [to, setTo] = useState("");

  const load = () => {
    setLoading(true);
    http.get("/digest/preview")
      .then(r => setPreview(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const sendTest = async (e) => {
    e?.preventDefault?.();
    if (!to) return toast.error("Enter an email");
    setBusy(true);
    try {
      const { data } = await http.post("/digest/send", { to });
      if (data.sent > 0) toast.success(`Sent to ${to}. Check inbox.`);
      else toast.error("Send failed. See console.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const sendAll = async () => {
    if (!window.confirm("Send this digest to ALL newsletter subscribers?")) return;
    setBusy(true);
    try {
      const { data } = await http.post("/digest/send", { all_subscribers: true });
      toast.success(`Sent to ${data.sent} of ${data.recipients} subscribers.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="digest-section" className="py-16 md:py-24 px-6 md:px-10 max-w-[1600px] mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-8">
        <div className="md:col-span-5">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">/ Weekly AI Digest</div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tighter flex items-center gap-3">
            <EnvelopeSimple weight="fill" className="text-[color:var(--np-brand)]" size={40} />
            Preview & send.
          </h2>
        </div>
        <p className="md:col-span-6 md:col-start-7 self-end text-[color:var(--np-text-2)] text-lg">
          NaturePulse composes an AI-curated digest of the top hazards, best stories, and community photos of the week. Preview here, send a test to any email, or blast to all subscribers.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 border border-black/15 bg-white overflow-hidden" data-testid="digest-preview-frame">
          {loading ? (
            <div className="h-[500px] flex items-center justify-center font-mono-tech text-xs tracking-widest uppercase text-[color:var(--np-text-2)]">Composing…</div>
          ) : (
            <iframe
              title="Digest preview"
              srcDoc={preview?.html}
              className="w-full"
              style={{ height: 600, border: "0" }}
            />
          )}
        </div>

        <div className="space-y-3">
          <div className="glass p-5">
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)] mb-1">Subject</div>
            <div className="font-serif-display text-xl leading-tight">{preview?.subject || "—"}</div>
          </div>

          <form onSubmit={sendTest} className="glass p-5 space-y-3">
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">Send test</div>
            <input
              type="email"
              value={to}
              onChange={e => setTo(e.target.value)}
              placeholder="tomvell@gmail.com"
              className="w-full bg-transparent border-b border-black/20 focus:border-[color:var(--np-brand)] outline-none py-2 font-mono-tech text-sm"
              data-testid="digest-test-email-input"
            />
            <button
              disabled={busy}
              className="w-full flex items-center justify-between font-mono-tech text-[11px] tracking-[0.3em] uppercase px-4 py-3 border border-black/20 hover:bg-[color:var(--np-text)] hover:text-[color:var(--np-bg)] transition-colors disabled:opacity-50"
              data-testid="digest-send-test-btn"
            >
              <span className="flex items-center gap-2"><PaperPlaneTilt size={14} weight="bold" />{busy ? "Sending…" : "Send test"}</span>
            </button>
          </form>

          <button
            onClick={sendAll}
            disabled={busy}
            className="w-full flex items-center justify-between font-mono-tech text-[11px] tracking-[0.3em] uppercase px-5 py-4 border bg-[color:var(--np-text)] text-[color:var(--np-bg)] border-[color:var(--np-text)] hover:bg-[color:var(--np-brand)] hover:border-[color:var(--np-brand)] transition-colors disabled:opacity-50"
            data-testid="digest-send-all-btn"
          >
            <span className="flex items-center gap-2"><PaperPlaneTilt size={14} weight="bold" />Send to all subscribers</span>
          </button>

          <button
            onClick={load}
            className="w-full flex items-center justify-between font-mono-tech text-[11px] tracking-[0.3em] uppercase px-4 py-3 border border-black/20 hover:bg-[color:var(--np-text)] hover:text-[color:var(--np-bg)] transition-colors"
            data-testid="digest-refresh-btn"
          >
            <span className="flex items-center gap-2"><ArrowsClockwise size={14} weight="bold" />Recompose</span>
          </button>
        </div>
      </div>
    </section>
  );
}
